import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Stack, Theme } from '@mui/material';
import { Button, Typography } from '@monorepo/nova-ui';

/**
 * UI state configuration for rule query rendering
 * Interface Segregation: Clean interface for UI state only
 */
export interface RuleQueryUIConfig {
  isLoading: boolean;
  error: Error | null;
  hasData: boolean;
  onRetry: () => void;
  errorMessage?: string;
  retryLabel?: string;
  noDataMessage?: string;
}

/**
 * Standardized UI components for rule query states
 * Single Responsibility: Handles UI rendering for query states only
 * Open/Closed: Extensible via config, closed for modification
 */
export const useRuleQueryUI = (config: RuleQueryUIConfig) => {
  const { t } = useTranslation(['common']);
  const {
    isLoading,
    error,
    hasData,
    onRetry,
    errorMessage = t('common:error.loading'),
    retryLabel = t('common:error.try_again'),
    noDataMessage = t('common:no_data'),
  } = config;

  /**
   * Renders loading state UI
   */
  const renderLoading = (): ReactNode => (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={(theme) => ({ minHeight: theme.spacing(5) })}
    >
      <CircularProgress size={20} />
    </Stack>
  );

  /**
   * Renders error state UI with retry button
   */
  const renderError = (theme: Theme): ReactNode => (
    <Stack gap={2} alignItems="flex-start">
      <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
        {errorMessage}
      </Typography>
      <Button
        variant="outlined"
        size="sm"
        onClick={onRetry}
        sx={{ minWidth: 'auto' }}
      >
        {retryLabel}
      </Button>
    </Stack>
  );

  /**
   * Renders no data state UI
   */
  const renderNoData = (theme: Theme): ReactNode => (
    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 40 }}>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        {noDataMessage}
      </Typography>
    </Stack>
  );

  /**
   * Determines which UI state to render
   * Returns null if data is available (caller should render data)
   */
  const getUIState = (theme: Theme): ReactNode | null => {
    if (isLoading) {
      return renderLoading();
    }

    if (error) {
      return renderError(theme);
    }

    if (!hasData) {
      return renderNoData(theme);
    }

    return null; // Has data - caller renders content
  };

  return {
    renderLoading,
    renderError,
    renderNoData,
    getUIState,
    isLoading,
    hasError: !!error,
    hasData,
  };
};
